import React, { CSSProperties, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import C from './JamCard.css';

import FlexRow from 'components/flex/FlexRow';
import VideoCard from 'components/video-card/VideoCard';

import { humanizeDurationSecsTime } from 'utils/date';

import { useCommunitySummary } from 'atoms/community-summaries';
import { getJamsLocal } from 'atoms/jams';
import CoCreatorCountTag from './CoCreatorCountTag';
import CountryOrCocoTag from './CountryOrCocoTag';
import { useSpaceSummary } from 'atoms/spaces';
import { DashboardParams } from 'constants/routes';
import { EVENTS, INTERACTIONS, useTrack } from 'utils/mixpanel';
import { JAM_TRANSLATION_MAP } from 'components/jam-details-modal/JamDetailsModal';
import { useCommunityDetails } from 'atoms/community-details';
import backgroundImage from '../../assets/images/icon--cocospace-light.svg';

export function WorldCorridorParticipantOverview(p: { spaceId: string }) {
  const { space: jam } = useSpaceSummary({
    spaceId: p.spaceId,
  });

  const { community } = useCommunitySummary({
    communityId: jam?.communityId ?? undefined,
  });

  const isTutorial = useMemo(
    () => jam?.themes?.find((t) => t?.toLocaleLowerCase() === 'tutorial'),
    [jam],
  );

  if (!jam) return null;
  return (
    <FlexRow
      style={{
        justifyContent: 'space-between',
        fontSize: 'var(--font-size-subtext)',
        marginBottom: '2px',
        width: '100%',
      }}
    >
      <FlexRow alignItems={'center'} gap={'8px'}>
        {isTutorial ? (
          <span
            style={{
              backgroundColor: 'var(--color-selected)',
              color: '#6a46c9',
              fontWeight: 'bold',
              padding: '0.1rem 0.8rem',
              borderRadius: '0.8rem',
            }}
          >
            Tutorial
          </span>
        ) : (
          <>
            <CoCreatorCountTag jam={jam} />
            <span
              className={C.communityName}
              style={{ textOverflow: 'ellipsis' }}
            >
              {community?.name ?? jam.createdByName}
            </span>
          </>
        )}
      </FlexRow>
      <CountryOrCocoTag jam={jam} />
    </FlexRow>
  );
}

export default function JamCard(p: {
  jamId: string;
  style?: CSSProperties;
  arrow?: boolean;
  containerWidth: number;
  previewHeight: number;
  showCommunityIcon?: boolean;
  showHostLabel?: boolean;
  showRecencyTag?: boolean;
  selected?: boolean;
  onClick?: () => void;
}) {
  const { space: jam } = useSpaceSummary({
    spaceId: p.jamId,
  });

  const { community } = useCommunityDetails({
    communityId: jam?.communityId ?? undefined,
    autoLoad: false,
  });

  const { communityId } = useParams<DashboardParams>();

  const history = useHistory();

  const navigateToSpace = () => {
    const isDashboard = history.location.pathname.includes('/dashboard');
    const isLanding = history.location.pathname == '/';

    if (isDashboard) {
      history.push(`/app/dashboard/${communityId}/jams/${jam.id}`);
    } else if (isLanding) {
      history.push(`/jams/${jam.id}`);
    } else {
      // Handle page
      history.push(
        `/@${community?.handle}/space/${jam?.communityId ? jam.spaceId : jam.id}`,
      );
    }
  };

  const track = useTrack();

  const jamClick = () => {
    track(EVENTS.JAM_CARD, INTERACTIONS.CLICK, {
      jamId: jam?.id,
      title: jam?.title,
    });

    if (p.onClick) p.onClick();
    else navigateToSpace();
  };

  if (!jam) return null;

  const translated = JAM_TRANSLATION_MAP()[jam?.title || ''] || jam;

  const hasBackground = jam.thumbnail || jam.bannerUrl;

  return (
    <div
      className={C.cardContainer}
      data-space-id={jam.id}
      style={{
        ...p.style,
        border: p.selected ? '2px solid var(--color-primary)' : undefined,
        flexBasis: p.containerWidth + 'px',
        backgroundColor: 'white',
        overflow: 'hidden',
      }}
    >
      {jam.jamVideoUrl ? (
        <VideoCard
          logo={'/' + jam.thumbnail}
          videoSrc={'/' + jam.jamVideoUrl}
          hoverAutoPlayOn
          onClick={() => {
            jamClick();
          }}
          style={{
            objectFit: 'cover',
          }}
        >
          <div className={C.jamTime}>
            {humanizeDurationSecsTime(jam?.jamVideoLength)}
          </div>
        </VideoCard>
      ) : (
        <div
          style={{
            backgroundImage: `url(${jam.thumbnail
              ? '/' + jam.thumbnail
              : jam.bannerUrl
                ? jam.bannerUrl
                : backgroundImage
              })`,
            backgroundSize: hasBackground ? 'cover' : '30%',
            paddingTop: '56.25%',
            ...(hasBackground
              ? {}
              : {
                backgroundColor: 'var(--color-base)',
                backgroundPosition: 'center center',
                backgroundRepeat: 'no-repeat',
              }),
          }}
          onClick={() => {
            jamClick();
          }}
        />
      )}

      <div className={C.cardDetails}>
        <FlexRow>
          <div
            className={C.title}
            onClick={() => {
              jamClick();
            }}
          >
            {translated.title}
          </div>
        </FlexRow>
        <WorldCorridorParticipantOverview spaceId={jam.id} />
      </div>
    </div>
  );
}
